import type { ComponentPublicInstance, MaybeRefOrGetter, Ref } from 'vue';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toValue,
  watch
} from 'vue';
import type { MediaItemInterface } from '@hypercodestudio/basler-components';

import {
  getMediaImagePreloadAttrs,
  getMediaImageSrc,
  isProgressiveImageCandidate,
  withMediaPreviewImage
} from '~/utils/mediaImageSrc';

type OverlayRoot = HTMLElement | ComponentPublicInstance;

type ProgressiveMediaOverlayOptions = {
  root: Ref<OverlayRoot | undefined>;
  previewWidth?: number;
  previewQuality?: number;
  marker?: string;
  preloadHighResInHead?: boolean;
  preloadKey?: string;
  mediaSelector?: string;
  imageSelector?: string;
};

const OVERLAY_PREVIEW_CLASS = 'has-progressive-preview';
const OVERLAY_LOADED_CLASS = 'is-progressive-image-loaded';
const PREVIEW_IMAGE_VAR = '--progressive-preview-image';

function toCssUrl(url: string): string {
  return `url("${url.replace(/"/g, '\\"')}")`;
}

function resolveRootElement(
  root: OverlayRoot | undefined
): HTMLElement | undefined {
  if (!root) {
    return undefined;
  }

  if (root instanceof HTMLElement) {
    return root;
  }

  const element = root.$el;
  return element instanceof HTMLElement ? element : undefined;
}

/**
 * Renders the real image immediately for LCP while using a low-res preview as a visual overlay.
 * Applies overlay state directly on `.media-image` so parent layout (e.g. floated breadcrumbs) stays intact.
 */
export function useProgressiveMediaOverlay(
  baseMedia: MaybeRefOrGetter<MediaItemInterface | undefined>,
  options: ProgressiveMediaOverlayOptions
) {
  const media = computed(() => toValue(baseMedia));
  const isPreviewVisible = ref(false);
  let overlayElement: HTMLElement | null = null;
  let removeImageListeners: (() => void) | undefined;

  const previewSrc = computed(() => {
    if (!isProgressiveImageCandidate(media.value)) {
      return undefined;
    }

    const previewMedia = withMediaPreviewImage(media.value!, {
      width: options.previewWidth,
      quality: options.previewQuality,
      marker: options.marker
    });

    return getMediaImageSrc(previewMedia)?.split('#')[0];
  });

  const highResPreloadAttrs = computed(() => {
    if (
      options.preloadHighResInHead !== true ||
      !isProgressiveImageCandidate(media.value)
    ) {
      return undefined;
    }

    return getMediaImagePreloadAttrs(media.value, 'avif');
  });

  if (options.preloadHighResInHead === true) {
    const preloadKey = options.preloadKey ?? 'progressive-media-image';
    useHead(() => ({
      link: highResPreloadAttrs.value
        ? [
            {
              key: `${preloadKey}-high-res`,
              rel: 'preload',
              as: 'image',
              fetchpriority: 'high',
              ...highResPreloadAttrs.value
            }
          ]
        : []
    }));
  }

  function findMediaImage(): HTMLElement | null {
    const rootElement = resolveRootElement(options.root.value);
    if (!rootElement) {
      return null;
    }

    return rootElement.querySelector<HTMLElement>(
      options.mediaSelector ?? '.media-image'
    );
  }

  function clearOverlayElement(): void {
    if (!overlayElement) {
      return;
    }

    overlayElement.classList.remove(
      OVERLAY_PREVIEW_CLASS,
      OVERLAY_LOADED_CLASS
    );
    overlayElement.style.removeProperty(PREVIEW_IMAGE_VAR);
    overlayElement = null;
  }

  function syncOverlayElement(): void {
    const mediaImage = findMediaImage();

    if (overlayElement && overlayElement !== mediaImage) {
      clearOverlayElement();
    }

    if (!mediaImage || !previewSrc.value) {
      clearOverlayElement();
      isPreviewVisible.value = false;
      return;
    }

    overlayElement = mediaImage;
    mediaImage.classList.add(OVERLAY_PREVIEW_CLASS);
    mediaImage.classList.toggle(OVERLAY_LOADED_CLASS, !isPreviewVisible.value);
    mediaImage.style.setProperty(PREVIEW_IMAGE_VAR, toCssUrl(previewSrc.value));
  }

  function clearImageListeners(): void {
    removeImageListeners?.();
    removeImageListeners = undefined;
  }

  function markImageLoaded(): void {
    isPreviewVisible.value = false;
    overlayElement?.classList.add(OVERLAY_LOADED_CLASS);
    clearImageListeners();
  }

  function bindImageLoadState(): void {
    clearImageListeners();
    syncOverlayElement();

    const mediaImage = overlayElement;
    if (!previewSrc.value || !mediaImage) {
      isPreviewVisible.value = false;
      return;
    }

    isPreviewVisible.value = true;
    mediaImage.classList.remove(OVERLAY_LOADED_CLASS);

    const image = mediaImage.querySelector<HTMLImageElement>(
      options.imageSelector ?? 'img'
    );

    if (!image) {
      return;
    }

    if (image.complete && image.naturalWidth > 0) {
      markImageLoaded();
      return;
    }

    image.addEventListener('load', markImageLoaded, { once: true });
    image.addEventListener('error', markImageLoaded, { once: true });
    removeImageListeners = () => {
      image.removeEventListener('load', markImageLoaded);
      image.removeEventListener('error', markImageLoaded);
    };
  }

  let nuxtApp: ReturnType<typeof useNuxtApp> | null = null;
  try {
    nuxtApp = useNuxtApp();
  } catch {
    nuxtApp = null;
  }

  function scheduleBindImageLoadState(): void {
    if (nuxtApp?.isHydrating) {
      nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
        void nextTick(bindImageLoadState);
      });
      return;
    }

    void nextTick(bindImageLoadState);
  }

  onMounted(() => {
    scheduleBindImageLoadState();
  });

  watch(
    [media, previewSrc],
    () => {
      scheduleBindImageLoadState();
    },
    { flush: 'post' }
  );

  watch(isPreviewVisible, (visible) => {
    overlayElement?.classList.toggle(OVERLAY_LOADED_CLASS, !visible);
  });

  onUnmounted(() => {
    clearImageListeners();
    clearOverlayElement();
  });

  return { media };
}
